import dayjs, { Dayjs } from 'dayjs';

import { DB_DATE_TIME_SHORT_FORMAT } from 'core/constants';
import { CorporatePartCostStatus } from 'corporate/enums';
import {
  ICorporatePart,
  ICorporatePartCostListItem,
  IGetActiveOverlappingCostPayload,
  IUpsertCorporatePartCostPayload,
} from 'corporate/types';

import { IUpsertCorporatePartCostForm } from './types';

const formatRequiredDate = (value: Dayjs): string => dayjs(value).format(DB_DATE_TIME_SHORT_FORMAT);

const formatOptionalDate = (value?: Dayjs | null): string | null => {
  if (!value) {
    return null;
  }

  return dayjs(value).format(DB_DATE_TIME_SHORT_FORMAT);
};

// PENDING edit = in-place update (send id, no status, preserve cost-item ids).
// Anything else (Add or Active edit) = clone-and-replace: BE expects a payload
// with no id and status='active' and treats it as supersede-the-active-cost.
// removedItems is only relevant for save (PENDING edits delete cost-item rows by id);
// the overlap-check endpoint ignores it (BE confirmed: only reads corporatePartId + startDate).
const buildBaseCostPayload = (
  data: IUpsertCorporatePartCostForm,
  corporatePart: Partial<ICorporatePart>,
  cost?: ICorporatePartCostListItem
): IGetActiveOverlappingCostPayload => {
  const isPendingEdit = cost?.status === CorporatePartCostStatus.PENDING;

  return {
    ...(isPendingEdit && cost?.id && { id: cost.id }),
    startDate: formatRequiredDate(data.startDate),
    endDate: formatOptionalDate(data.endDate),
    uom: data.uom?.name,
    costItems: data.costItems.map((item) => ({
      ...(isPendingEdit && item.id && { id: item.id }),
      cost: item.cost,
      minQuantity: item.minQuantity,
      maxQuantity: item.maxQuantity ?? null,
    })),
    corporatePartId: corporatePart.id,
    part: corporatePart.part,
    manufacturerId: corporatePart.manufacturerId,
    ...(!isPendingEdit && { status: CorporatePartCostStatus.ACTIVE }),
  };
};

export const mapUpsertCorporatePartCost = (
  data: IUpsertCorporatePartCostForm,
  corporatePart: Partial<ICorporatePart>,
  cost?: ICorporatePartCostListItem,
  removedItems: number[] = []
): IUpsertCorporatePartCostPayload => {
  const isPendingEdit = cost?.status === CorporatePartCostStatus.PENDING;

  return {
    ...buildBaseCostPayload(data, corporatePart, cost),
    ...(isPendingEdit && removedItems.length > 0 && { removedItems }),
  };
};

export const mapActiveOverlappingCostPayload = (
  data: IUpsertCorporatePartCostForm,
  corporatePart: Partial<ICorporatePart>,
  cost?: ICorporatePartCostListItem
): IGetActiveOverlappingCostPayload => buildBaseCostPayload(data, corporatePart, cost);

export const getDefaultValues = (
  corporatePart: Partial<ICorporatePart>,
  cost?: ICorporatePartCostListItem
): IUpsertCorporatePartCostForm => {
  const defaultUomName = cost?.uom ?? corporatePart?.purchaseCostUom ?? null;
  const defaultUom = defaultUomName ? { id: defaultUomName, name: defaultUomName } : null;

  if (cost) {
    const isPendingEdit = cost.status === CorporatePartCostStatus.PENDING;

    return {
      // Pending edits keep the original future start. Active edits (clone-and-replace)
      // start a brand-new active cost, so default startDate to today.
      startDate: isPendingEdit && cost.startDate ? dayjs(cost.startDate) : dayjs(),
      endDate: cost.endDate ? dayjs(cost.endDate) : null,
      uom: defaultUom,
      costItems: cost.costItems?.map((item) => ({
        id: item.id,
        cost: item.cost,
        minQuantity: item.minQuantity,
        maxQuantity: item.maxQuantity,
      })) ?? [{ minQuantity: undefined, maxQuantity: undefined, cost: undefined }],
    };
  }

  return {
    startDate: dayjs(),
    endDate: null,
    uom: defaultUom,
    costItems: [
      {
        minQuantity: corporatePart?.orderIncrement ?? 1,
        maxQuantity: undefined,
        cost: undefined,
      },
    ],
  };
};
